/**
 * 画板选中操作 / 编辑定型图形 / 样式回写
 * （selectedId 状态由编排层持有，便于 layers 同步引用）
 */
import { useCallback, useEffect } from 'react';
import { message } from '../utils/antdMessage';
import {
  DEFAULT_TEXT_STYLE,
  openRingCoords,
  isAreaFeatureType,
  isEditableFeatureType,
  featureFromEditMeta
} from '../utils/mapDrawingBoard';

export default function useMapDrawingSelection({
  getMap,
  onStartInteract,
  features,
  setFeatures,
  featuresRef,
  draftRef,
  toolRef,
  editingIdRef,
  editingMetaRef,
  selectedId,
  selectedIds,
  selectedIdRef,
  selectedIdsRef,
  setSelectedId,
  setSelectedIds,
  syncFeaturesToMap,
  syncPreview,
  clearPreview,
  setDraftPoints,
  setTool,
  setDrawColor,
  setDrawOpacity,
  setSketchLineWidth,
  setTextContent,
  setTextStyle,
  setTextEditOpen,
  setExpanded,
  setPinned,
  setCtxMenu,
  setStatusText,
  setIsEditingPath,
  drawColor,
  drawOpacity,
  sketchLineWidth
}) {
  /** 更新当前选中（支持多选批量）图形字段 */
  const patchSelectedFeature = useCallback((patch) => {
    const ids = selectedIdsRef.current;
    if (!ids.length) return;
    const idSet = new Set(ids);
    const map = getMap?.();
    const next = featuresRef.current.map((f) => (
      idSet.has(f.id) ? { ...f, ...patch } : f
    ));
    setFeatures(next);
    featuresRef.current = next;
    if (map) syncFeaturesToMap(map, next);
  }, [getMap, syncFeaturesToMap, selectedIdsRef, featuresRef, setFeatures]);

  // 选中变化时刷新高亮
  useEffect(() => {
    const map = getMap?.();
    if (map && featuresRef.current) {
      syncFeaturesToMap(map, featuresRef.current);
    }
  }, [selectedIds, getMap, syncFeaturesToMap, featuresRef]);

  /** 删除当前选中的定型图形（支持多选） */
  const deleteSelectedFeature = useCallback(() => {
    const ids = selectedIdsRef.current;
    if (!ids.length) return false;
    if (ids.some((id) => editingIdRef.current === id)) return false;
    const map = getMap?.();
    const idSet = new Set(ids);
    const next = featuresRef.current.filter((f) => !idSet.has(f.id));
    if (next.length === featuresRef.current.length) return false;
    setFeatures(next);
    featuresRef.current = next;
    setSelectedIds([]);
    if (map) syncFeaturesToMap(map, next);
    message.success(ids.length > 1 ? `已删除 ${ids.length} 项` : '已删除');
    setStatusText(ids.length > 1 ? `已删除选中的 ${ids.length} 个图形` : '已删除选中图形');
    return true;
  }, [getMap, syncFeaturesToMap, setSelectedIds, selectedIdsRef, editingIdRef, featuresRef, setFeatures, setStatusText]);

  /** 将定型图形拉回草稿再编辑（路线/测距/区域/矩形）；文字打开编辑框；手绘仅样式 */
  const startEditFeature = useCallback((feature) => {
    if (!feature || !isEditableFeatureType(feature.type)) {
      message.info('该图形不支持编辑');
      return;
    }
    if (feature.type === 'text') {
      onStartInteract?.();
      setSelectedId(feature.id);
      setTextContent(feature.text || '');
      setTextStyle({
        fontFamily: feature.fontFamily || DEFAULT_TEXT_STYLE.fontFamily,
        fontSize: feature.fontSize || DEFAULT_TEXT_STYLE.fontSize,
        letterSpacing: feature.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing,
        lineHeight: feature.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight,
        color: feature.color || DEFAULT_TEXT_STYLE.color,
        bgColor: feature.bgColor || DEFAULT_TEXT_STYLE.bgColor,
        bgEnabled: feature.bgEnabled !== false,
        opacity: feature.opacity ?? DEFAULT_TEXT_STYLE.opacity
      });
      setDrawColor(feature.color || DEFAULT_TEXT_STYLE.color);
      setExpanded(true);
      setPinned(true);
      setTextEditOpen(true);
      setStatusText('编辑文字内容与样式');
      return;
    }
    if (feature.type === 'sketch') {
      onStartInteract?.();
      setSelectedId(feature.id);
      setDrawColor(feature.color);
      setDrawOpacity(feature.opacity);
      if (feature.lineWidth) setSketchLineWidth(feature.lineWidth);
      setExpanded(true);
      setPinned(true);
      setStatusText('手绘可选中改颜色/线宽，或删除后重画');
      message.info('手绘支持改样式与删除；形状需删除后重画');
      return;
    }
    const map = getMap?.();
    onStartInteract?.();
    setCtxMenu(null);
    // 从定型列表暂移出，避免与预览重叠
    const next = featuresRef.current.filter((f) => f.id !== feature.id);
    setFeatures(next);
    featuresRef.current = next;
    if (map) syncFeaturesToMap(map, next);

    const pts = isAreaFeatureType(feature.type)
      ? openRingCoords(feature.coordinates || []).map((c) => [...c])
      : (feature.coordinates || []).map((c) => [...c]);

    editingIdRef.current = feature.id;
    editingMetaRef.current = {
      type: feature.type,
      name: feature.name,
      color: feature.color,
      opacity: feature.opacity,
      fill: feature.fill,
      coordinates: pts.map((c) => [...c])
    };
    setIsEditingPath(true);
    setDraftPoints(pts);
    draftRef.current = pts;
    setDrawColor(feature.color);
    setDrawOpacity(feature.opacity);
    setSelectedId(feature.id);

    // 区域/矩形统一用 polygon 多点编辑（矩形新建仍是对角两点）
    const editTool = feature.type === 'measure'
      ? 'measure'
      : feature.type === 'route'
        ? 'route'
        : 'polygon';
    setTool(editTool);
    toolRef.current = editTool;
    if (map) syncPreview(map, pts, editTool);
    setExpanded(true);
    setPinned(true);
    setStatusText(
      isAreaFeatureType(feature.type)
        ? '编辑形状：拖动橙色顶点；点在边上可插入点；右键/「保存修改」保存，取消则还原'
        : '编辑路径：拖动橙色顶点；点在线上可插入点；右键/「保存修改」保存，取消则还原'
    );
  }, [getMap, syncFeaturesToMap, syncPreview, onStartInteract, setSelectedId, setTextContent, setTextStyle, setDrawColor, setExpanded, setPinned, setTextEditOpen, setStatusText, setDrawOpacity, setSketchLineWidth, setCtxMenu, featuresRef, editingIdRef, editingMetaRef, setIsEditingPath, setDraftPoints, draftRef, setTool, toolRef, setFeatures]);

  /** 取消绘制；若在编辑已定型图形，则还原为进入编辑前的形状 */
  const cancelCurrentDrawing = useCallback(() => {
    const map = getMap?.();
    const editingId = editingIdRef.current;
    const meta = editingMetaRef.current;
    if (editingId && meta && meta.coordinates && meta.coordinates.length >= 2) {
      const restored = featureFromEditMeta(editingId, meta);
      const next = [...featuresRef.current.filter((f) => f.id !== editingId), restored];
      setFeatures(next);
      featuresRef.current = next;
      if (map) syncFeaturesToMap(map, next);
      setSelectedId(editingId);
      setStatusText('已取消编辑，恢复原图形');
    } else {
      setStatusText('已取消当前绘制');
    }
    editingIdRef.current = null;
    editingMetaRef.current = null;
    setIsEditingPath(false);
    clearPreview();
  }, [getMap, syncFeaturesToMap, clearPreview, editingIdRef, editingMetaRef, featuresRef, setFeatures, setSelectedId, setStatusText, setIsEditingPath]);

  // 选中项改色/透明度写回（支持多选批量；文字同步 color 字段）
  useEffect(() => {
    const ids = selectedIdsRef.current;
    if (!ids.length) return;
    const map = getMap?.();
    const idSet = new Set(ids);
    let touchedText = false;
    const next = featuresRef.current.map((f) => {
      if (!idSet.has(f.id)) return f;
      if (f.type === 'text') touchedText = true;
      return { ...f, color: drawColor, opacity: drawOpacity };
    });
    if (touchedText) {
      setTextStyle((s) => (s.color === drawColor && s.opacity === drawOpacity
        ? s
        : { ...s, color: drawColor, opacity: drawOpacity }));
    }
    if (JSON.stringify(next) === JSON.stringify(featuresRef.current)) return;
    setFeatures(next);
    featuresRef.current = next;
    if (map) syncFeaturesToMap(map, next);
  }, [drawColor, drawOpacity, selectedIds, getMap, syncFeaturesToMap, selectedIdsRef, featuresRef, setTextStyle, setFeatures]);

  // 选中手绘时同步线宽（多选时批量应用到手绘）
  useEffect(() => {
    const ids = selectedIdsRef.current;
    if (!ids.length) return;
    const idSet = new Set(ids);
    const need = featuresRef.current.some(
      (f) => idSet.has(f.id) && f.type === 'sketch' && f.lineWidth !== sketchLineWidth
    );
    if (!need) return;
    patchSelectedFeature({ lineWidth: sketchLineWidth });
  }, [sketchLineWidth, selectedIds, patchSelectedFeature, selectedIdsRef, featuresRef]);

  // 选中切换时：把文字/手绘样式灌入侧栏
  useEffect(() => {
    if (!selectedId) return;
    const feat = features.find((f) => f.id === selectedId);
    if (!feat) return;
    if (feat.type === 'text') {
      setTextContent(feat.text || '');
      setTextStyle({
        fontFamily: feat.fontFamily || DEFAULT_TEXT_STYLE.fontFamily,
        fontSize: feat.fontSize || DEFAULT_TEXT_STYLE.fontSize,
        letterSpacing: feat.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing,
        lineHeight: feat.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight,
        color: feat.color || DEFAULT_TEXT_STYLE.color,
        bgColor: feat.bgColor || DEFAULT_TEXT_STYLE.bgColor,
        bgEnabled: feat.bgEnabled !== false,
        opacity: feat.opacity ?? DEFAULT_TEXT_STYLE.opacity
      });
    }
    if (feat.type === 'sketch' && feat.lineWidth) {
      setSketchLineWidth(feat.lineWidth);
    }
  // 仅在切换选中时灌入样式，不跟随 features 全量刷新
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return {
    patchSelectedFeature,
    deleteSelectedFeature,
    startEditFeature,
    cancelCurrentDrawing
  };
}
